/**
 * Spreadsheet-style keyboard navigation + cell focus for the Master &
 * Manager boards. Focused on fast VISUAL scanning, NOT inline editing.
 *
 * Usage:
 *   const grid = useSpreadsheetGrid({ onEnter: (rowKey) => openQuickEdit(rowKey) });
 *   ...
 *   <table {...grid.tableProps}>
 *     <tbody>
 *       {rows.map(row => (
 *         <tr key={row.id}>
 *           <td {...grid.cellProps({ rowKey: row.id, colKey: "due_date" })}>...</td>
 *         </tr>
 *       ))}
 *     </tbody>
 *   </table>
 *
 * The hook is a pure keyboard/focus manager. It does NOT own any styling —
 * the caller's CSS should style `[data-spreadsheet-cell]:focus-visible`.
 */
import { useCallback, useMemo, useRef } from "react";

const NAVIGATION_KEYS = new Set([
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
  "Home", "End", "PageUp", "PageDown",
]);

function collectCells(container) {
  return Array.from(container.querySelectorAll("td[data-spreadsheet-cell='true']"));
}

function groupCellsByRow(cells) {
  const rows = [];
  const rowIndex = new Map();
  for (const cell of cells) {
    const rowKey = cell.getAttribute("data-cell-row") || "";
    if (!rowIndex.has(rowKey)) {
      rowIndex.set(rowKey, rows.length);
      rows.push({ rowKey, cells: [] });
    }
    rows[rowIndex.get(rowKey)].cells.push(cell);
  }
  return rows;
}

export function useSpreadsheetGrid({ onEnter } = {}) {
  const containerRef = useRef(null);

  const focusCell = useCallback((rowIdx, colIdx) => {
    const container = containerRef.current;
    if (!container) return;
    const rows = groupCellsByRow(collectCells(container));
    if (rows.length === 0) return;
    const clampedRow = Math.max(0, Math.min(rowIdx, rows.length - 1));
    const targetRow = rows[clampedRow];
    const clampedCol = Math.max(0, Math.min(colIdx, targetRow.cells.length - 1));
    const cell = targetRow.cells[clampedCol];
    if (cell) cell.focus();
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      // Ignore keys inside real editable elements (inputs, textareas, editors)
      if (target.matches("input, textarea, select, [contenteditable='true']")) return;
      const cell = target.closest("td[data-spreadsheet-cell='true']");
      if (!cell) return;

      const isNav = NAVIGATION_KEYS.has(event.key);
      const isActivate = event.key === "Enter";
      const isEscape = event.key === "Escape";
      if (!isNav && !isActivate && !isEscape) return;

      const container = containerRef.current;
      if (!container) return;
      const rows = groupCellsByRow(collectCells(container));
      if (rows.length === 0) return;

      const currentRowKey = cell.getAttribute("data-cell-row") || "";
      const rowIdx = rows.findIndex((r) => r.rowKey === currentRowKey);
      if (rowIdx === -1) return;
      const colIdx = rows[rowIdx].cells.indexOf(cell);

      if (isEscape) {
        cell.blur();
        return;
      }
      if (isActivate) {
        event.preventDefault();
        onEnter?.(currentRowKey);
        return;
      }

      let nextRow = rowIdx;
      let nextCol = colIdx;
      switch (event.key) {
        case "ArrowUp":    nextRow = rowIdx - 1; break;
        case "ArrowDown":  nextRow = rowIdx + 1; break;
        case "ArrowLeft":  nextCol = colIdx - 1; break;
        case "ArrowRight": nextCol = colIdx + 1; break;
        case "Home":       nextCol = 0; break;
        case "End":        nextCol = rows[rowIdx].cells.length - 1; break;
        case "PageUp":     nextRow = Math.max(0, rowIdx - 10); break;
        case "PageDown":   nextRow = Math.min(rows.length - 1, rowIdx + 10); break;
        default: return;
      }
      event.preventDefault();
      focusCell(nextRow, nextCol);
    },
    [focusCell, onEnter],
  );

  const tableProps = useMemo(
    () => ({
      ref: containerRef,
      onKeyDown,
      role: "grid",
    }),
    [onKeyDown],
  );

  const cellProps = useCallback(
    ({ rowKey, colKey }) => ({
      "data-spreadsheet-cell": "true",
      "data-cell-row": rowKey,
      "data-cell-col": colKey,
      tabIndex: -1,
      role: "gridcell",
    }),
    [],
  );

  return { tableProps, cellProps, focusCell };
}
